import React, { useCallback, useEffect, useState } from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import { Form, Dropdown, Message, Grid, Confirm } from 'semantic-ui-react';
import { useAuth } from '../../../providers/AuthenticationProvider';
import { useUsers } from '../../../providers/UsersProvider';
import { useDistributors } from '../../../providers/DistributorProvider';
const WEB_PAGE = 'Users';

export const CreateUser = props => {
    const { isSystemAdmin, checkAuth, getUserType } = useAuth();
    const location = useLocation();
    const userObject = location?.state?.user;
    const [userRole, setUserRole] = useState(userObject?.roleId);
    const [distributorId, setDistributorId] = useState(userObject?.distributorId);
    const { getRoles, roles, createUser, updateUser, toggleUserActive, deleteUser } = useUsers();
    const { getAllDistributors } = useDistributors();
    const [sendInvite, setSendInvite] = useState(true);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [notes, setNotes] = useState(userObject?.notes ?? '');
    const [errors, setErrors] = useState();
    const [loading, setLoading] = useState(false);
    const [disableLoading, setDisableLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [distributors, setDistributors] = useState([]);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const navigate = useNavigate();

    checkAuth(getUserType(), WEB_PAGE) && navigate('/');

    useEffect(() => {
        if (isSystemAdmin()) {
            getRoles();
        }
    }, [getRoles, isSystemAdmin]);

    const fetchDistributors = useCallback(async () => {
        if (isSystemAdmin()) {
            const response = await getAllDistributors();
            const data = response.data.map(entry => {
                return { key: entry.id, text: entry.name, value: entry.id };
            });
            setDistributors(data);
        }
    }, [getAllDistributors, isSystemAdmin]);

    useEffect(() => {
        if (isSystemAdmin) {
            fetchDistributors();
        }
    }, [isSystemAdmin, fetchDistributors]);

    useEffect(() => {
        const systemAdminRoleId = roles.find(role => role.text === 'System Administrator')?.value;
        if (userRole === systemAdminRoleId && distributorId != null) {
            setDistributorId();
        }
    }, [userRole, roles, distributorId])

    const handleUpdate = e => {
        e.preventDefault();

        const firstName = e.target[0].value;
        const lastName = e.target[1].value;
        const emailAddress = e.target[2].value;
        const phoneNumber = e.target[3].value;
        const notes = e.target[4].value;

        if (!userRole && isSystemAdmin()) {
            setErrors(['Must select a user role.']);
            return;
        }

        if (phoneNumber.length !== 10) {
            setErrors(['Phone Number must be 10 digits.']);
            return;
        }
        setLoading(true);
        updateUser(userObject?.id, notes, firstName, lastName, emailAddress, phoneNumber, userRole, distributorId)
            .then(result => {
                navigate('/users');
                setLoading(false);
            })
            .catch(err => {
                if (err?.response?.data?.errors) {
                    setErrors(
                        err.response.data.errors.map(error => {
                            return error.description;
                        })
                    );
                } else {
                    setErrors(['Failed to update a new user.']);
                }
                setLoading(false);
            });
    };

    const handleSubmit = e => {
        e.preventDefault();

        //create user
        const firstName = e.target[0].value;
        const lastName = e.target[1].value;
        const emailAddress = e.target[2].value;
        const phoneNumber = e.target[3].value;

        if (!userRole && isSystemAdmin()) {
            setErrors(['Must select a user role.']);
            return;
        }

        if (phoneNumber.length !== 10) {
            setErrors(['Phone Number must be 10 digits.']);
            return;
        }

        if (!sendInvite && password !== confirmPassword) {
            setErrors(['Password and Confirm Password must match']);
            return;
        }

        setLoading(true);
        createUser(notes, firstName, lastName, emailAddress, phoneNumber, userRole, sendInvite ? null : password, distributorId)
            .then(result => {
                navigate('/users');
                setLoading(false);
            })
            .catch(err => {
                if (err?.response?.data?.errors) {
                    setErrors(
                        err.response.data.errors.map(error => {
                            return error.description;
                        })
                    );
                } else {
                    setErrors(['Failed to create a new user.']);
                }
                setLoading(false);
            });
    };

    const handleDelete = async () => {
        setDeleteLoading(true);
        try {
            await deleteUser(userObject.id);
            setShowDeleteConfirmation(false)
            setDeleteLoading(false);
            navigate('/users');
        } catch (err) {
            setErrors(['Failed to delete user, please try again later.']);
            setShowDeleteConfirmation(false)
            setDeleteLoading(false);
        }
    };

    return (
        <div>
            <Confirm
                open={showDeleteConfirmation}
                onCancel={() => setShowDeleteConfirmation(false)}
                onConfirm={handleDelete}
                content={"Are you sure you want to delete this user and all it's associated data?"}
                confirmButton="Confirm"
            />
            <div className="pageHeader">
                <h1>{userObject ? 'Edit' : 'Create'} User</h1>
            </div>
            <Form onSubmit={userObject ? handleUpdate : handleSubmit} error={errors ? true : false} style={{ maxWidth: '800px' }}>
                <Form.Group widths="equal">
                    <Form.Input defaultValue={userObject?.firstname} label="First Name" required />
                    <Form.Input defaultValue={userObject?.lastname} label="Last Name" required />
                </Form.Group>
                <Form.Group widths="equal">
                    <Form.Input
                        defaultValue={userObject?.email}
                        label="Email Address"
                        type="email"
                        required
                    />
                    <Form.Input
                        defaultValue={userObject?.phoneNumber}
                        onChange={e => {
                            if (e.target.value == null || e.target.value === '') return;
                            let val = e.target.value.replace(/\D/g, '');
                            if (val.length > 10) {
                                val = val.substring(0, 10);
                            }
                            e.target.value = val;
                        }}
                        label="Phone Number"
                        type="phone"
                        required
                    />
                </Form.Group>
                {!userObject ? (
                    <>
                        <Form.Group>
                            <Form.Field>
                                <label>Password Setup</label>
                                <Form.Radio
                                    label="Send invite email (user sets their own password)"
                                    checked={sendInvite}
                                    onChange={() => setSendInvite(true)}
                                />
                                <Form.Radio
                                    label="Set password manually"
                                    checked={!sendInvite}
                                    onChange={() => setSendInvite(false)}
                                />
                            </Form.Field>
                        </Form.Group>
                        {!sendInvite && (
                            <Form.Group widths="equal">
                                <Form.Input
                                    label="Password"
                                    type="password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                />
                                <Form.Input
                                    label="Confirm Password"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                />
                            </Form.Group>
                        )}
                    </>
                ) : null}
                {isSystemAdmin() ? (
                    <Form.Group widths="equal">
                        <Form.Field
                            label={'User Role'}
                            control={Dropdown}
                            defaultValue={userObject?.roleId}
                            style={{ maxHeight: '38px' }}
                            fluid
                            selection
                            onChange={(e, selection) => {
                                setUserRole(selection.value);
                            }}
                            options={roles}
                            required
                        />
                        <Form.Field
                            label={'Distributor'}
                            control={Dropdown}
                            defaultValue={userObject?.distributorId}
                            style={{ maxHeight: '38px' }}
                            fluid
                            selection
                            onChange={(e, selection) => {
                                setDistributorId(selection.value);
                            }}
                            options={distributors}
                            required={userRole !== roles.find(role => role.text === 'System Administrator')?.value}
                            disabled={!userRole || userRole === roles.find(role => role.text === 'System Administrator')?.value}
                        />
                    </Form.Group>
                ) : null}
                <Form.Group>
                    <Form.TextArea width={16} value={notes} onChange={e => setNotes(e.target.value)} label="Notes" />
                </Form.Group>
                {errors ? (
                    <Form.Group widths={2} style={{ paddingLeft: '8px' }}>
                        <Message error list={errors} />
                    </Form.Group>
                ) : null}
                <Grid>
                    <Grid.Column style={{ display: 'flex' }} width={6}>
                        <Form.Group>
                            <Form.Button
                                size={'large'}
                                type={'submit'}
                                color={'blue'}
                                loading={loading}
                            >
                                {userObject ? 'Update' : 'Submit'}
                            </Form.Button>
                            <Form.Button
                                size={'large'}
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate('/users');
                                }}
                            >
                                Cancel
                            </Form.Button>
                        </Form.Group>
                    </Grid.Column>
                    <Grid.Column style={{ display: 'flex', justifyContent: 'flex-end' }} width={6}>
                        <Form.Group>
                            {userObject?.id ? (
                                <Form.Button
                                    size={'large'}
                                    loading={disableLoading}
                                    onClick={e => {
                                        e.preventDefault();
                                        setDisableLoading(true);
                                        toggleUserActive(userObject.id, !userObject.inactive)
                                            .then(result => {
                                                userObject.inactive = !userObject.inactive;
                                                setDisableLoading(false);
                                            })
                                            .catch(err => {
                                                setDisableLoading(false);
                                            });
                                    }}
                                    negative={!userObject.inactive}
                                    positive={userObject.inactive}
                                >
                                    {userObject.inactive ? 'Activate' : 'Deactivate'}
                                </Form.Button>
                            ) : null}
                            {userObject?.id && isSystemAdmin() ? (
                                <Form.Button
                                    size={'large'}
                                    loading={deleteLoading}
                                    onClick={e => {
                                        e.preventDefault();
                                        setShowDeleteConfirmation(true);
                                    }}
                                    negative
                                >
                                    Delete
                                </Form.Button>
                            ) : null}
                        </Form.Group>
                    </Grid.Column>
                </Grid>
            </Form>
        </div>
    );
};
